/**
 * The data table, as finixt-ui's Table draws it: a bordered card, a sunken
 * mono head, hairline rows, no zebra, no vertical rules, and the footer the
 * prototype adds with what you are looking at (`count`) and one line of
 * context (`hint`) or the pager.
 *
 * Columns are finixt-ui's shape, typed over the row. The engine is local
 * because the package's head cells and rows cannot be reached for the two
 * things an operator console needs: a sortable head is a button and the
 * cell says `aria-sort`; a clickable row takes focus and activates on Enter.
 * A row that also has a full page goes there on a double-click or
 * Shift+Enter (optum-agent Feature 87, D17, D18).
 */
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';

import { Icon, Typography } from './finixt.js';

const DENSITY = { compact: '7px 12px', default: '10px 14px', relaxed: '13px 16px' } as const;

export interface DataColumn<Row> {
  key: string;
  label: ReactNode;
  /** Right-align numeric columns. */
  align?: 'left' | 'right' | 'center' | undefined;
  /** Geist Mono: any number, id, or version. */
  mono?: boolean | undefined;
  /** Semibold in the strong text color; the identifying column only. */
  strong?: boolean | undefined;
  /** Allow the cell to wrap. Cells are nowrap by default. */
  wrap?: boolean | undefined;
  width?: number | string | undefined;
  sortable?: boolean | undefined;
  /** A cell that holds controls: its clicks and double-clicks do not reach the row. */
  stopRowClick?: boolean | undefined;
  render?: ((row: Row) => ReactNode) | undefined;
}

export interface DataTableProps<Row> {
  columns: DataColumn<Row>[];
  rows: Row[];
  /** The field used as the React key, or a function of the row. Default `id`. */
  rowKey?: (keyof Row & string) | ((row: Row) => string) | undefined;
  density?: keyof typeof DENSITY | undefined;
  onRowClick?: ((row: Row) => void) | undefined;
  /** A double-click or Shift+Enter on a row: its record's full page. */
  onRowOpenPage?: ((row: Row) => void) | undefined;
  /** The selected row draws the accent wash and a 2px accent inset edge. */
  selectedKey?: string | undefined;
  sortKey?: string | undefined;
  sortDir?: 'asc' | 'desc' | undefined;
  onSort?: ((key: string, dir: 'asc' | 'desc') => void) | undefined;
  /** Rendered in place of the body when rows is empty. */
  empty?: ReactNode;
  /** A style for a row by its state: a dimmed history row, a tinted receipt. */
  rowStyle?: ((row: Row) => CSSProperties | undefined) | undefined;
  /** Mono, left in the footer: "12 of 340 agents". */
  count?: ReactNode;
  /** Mono eyebrow, right in the footer. */
  hint?: string | undefined;
  /** Page controls for a server-paged list; takes the hint's place. */
  pager?: ReactNode;
}

function keyOf<Row>(row: Row, rowKey: DataTableProps<Row>['rowKey'], index: number): string {
  if (typeof rowKey === 'function') return rowKey(row);
  const field = (rowKey ?? 'id') as keyof Row;
  const value = (row as Record<keyof Row, unknown>)[field];
  return value === undefined || value === null ? String(index) : String(value);
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  density = 'default',
  onRowClick,
  onRowOpenPage,
  selectedKey,
  sortKey,
  sortDir = 'asc',
  onSort,
  empty,
  rowStyle,
  count,
  hint,
  pager,
}: DataTableProps<Row>) {
  const pad = DENSITY[density];
  const footer =
    count || hint || pager ? (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 11,
          padding: '7px 14px',
          background: 'var(--surface-sunken)',
          borderTop: '1px solid var(--border-hairline)',
        }}
      >
        <span
          className="mono"
          style={{ fontSize: 'var(--text-label-lg)', color: 'var(--text-faint)' }}
        >
          {count}
          {pager && hint ? ` · ${hint}` : null}
        </span>
        {pager ?? (hint ? <Typography variant="eyebrow">{hint}</Typography> : null)}
      </div>
    ) : null;

  return (
    <div
      className="ui-table"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
      }}
    >
      {rows.length === 0 && empty ? (
        empty
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
            <thead>
              <tr>
                {columns.map((column) => {
                  const sorted = sortKey === column.key;
                  const sortable = Boolean(column.sortable && onSort);
                  const nextDir = sorted && sortDir === 'asc' ? 'desc' : 'asc';
                  const label = (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 2,
                        justifyContent: column.align === 'right' ? 'flex-end' : undefined,
                      }}
                    >
                      {column.label}
                      {sortable ? (
                        <Icon
                          name={
                            sorted
                              ? sortDir === 'asc'
                                ? 'arrow_upward'
                                : 'arrow_downward'
                              : 'unfold_more'
                          }
                          size={13}
                          color={sorted ? 'var(--accent)' : 'var(--grey-600)'}
                        />
                      ) : null}
                    </span>
                  );
                  return (
                    <th
                      key={column.key}
                      aria-sort={
                        sortable
                          ? sorted
                            ? sortDir === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                          : undefined
                      }
                      style={{
                        background: 'var(--surface-sunken)',
                        borderBottom: '1px solid var(--border-hairline)',
                        padding: pad,
                        textAlign: column.align ?? 'left',
                        whiteSpace: 'nowrap',
                        width: column.width,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--text-label-sm)',
                        fontWeight: 'var(--weight-semibold)',
                        letterSpacing: 'var(--tracking-label-tight)',
                        textTransform: 'uppercase',
                        color: sorted ? 'var(--text-body)' : 'var(--text-faint)',
                      }}
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={() => onSort?.(column.key, nextDir)}
                          style={{
                            all: 'unset',
                            cursor: 'pointer',
                            font: 'inherit',
                            letterSpacing: 'inherit',
                            textTransform: 'inherit',
                            color: 'inherit',
                          }}
                        >
                          {label}
                        </button>
                      ) : (
                        label
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const key = keyOf(row, rowKey, index);
                const selected = selectedKey !== undefined && selectedKey === key;
                const clickable = onRowClick !== undefined;
                function onKeyDown(event: KeyboardEvent<HTMLTableRowElement>) {
                  if (event.key !== 'Enter' || event.target !== event.currentTarget) return;
                  if (event.shiftKey && onRowOpenPage) onRowOpenPage(row);
                  else onRowClick?.(row);
                }
                return (
                  <tr
                    key={key}
                    data-clickable={clickable ? '' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={clickable ? () => onRowClick(row) : undefined}
                    onDoubleClick={onRowOpenPage ? () => onRowOpenPage(row) : undefined}
                    onKeyDown={clickable ? onKeyDown : undefined}
                    style={{
                      background: selected ? 'var(--accent-wash)' : undefined,
                      boxShadow: selected ? 'inset 2px 0 0 var(--accent)' : undefined,
                      cursor: clickable ? 'pointer' : undefined,
                      transition: 'background var(--dur-micro) var(--ease-standard)',
                      ...rowStyle?.(row),
                    }}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        onClick={
                          column.stopRowClick ? (event) => event.stopPropagation() : undefined
                        }
                        onDoubleClick={
                          column.stopRowClick ? (event) => event.stopPropagation() : undefined
                        }
                        style={{
                          padding: pad,
                          borderBottom:
                            index === rows.length - 1 ? 'none' : '1px solid var(--border-divider)',
                          textAlign: column.align ?? 'left',
                          verticalAlign: 'middle',
                          fontFamily: column.mono ? 'var(--font-mono)' : 'var(--font-sans)',
                          fontSize: column.mono ? 'var(--text-meta-lg)' : 'var(--text-body-sm)',
                          fontWeight: column.strong
                            ? 'var(--weight-semibold)'
                            : 'var(--weight-regular)',
                          color: column.strong ? 'var(--text-strong)' : 'var(--text-muted)',
                          whiteSpace: column.wrap ? 'normal' : 'nowrap',
                        }}
                      >
                        {column.render
                          ? column.render(row)
                          : String((row as Record<string, unknown>)[column.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {footer}
    </div>
  );
}
